import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink, Star, Loader2 } from "lucide-react";
import { useConsultationExperts } from "@/hooks/use-consultation-experts";
import { ExpertsEmptyState } from "./ExpertsEmptyState";

export function ExpertProfiles() {
  const { data: experts, isLoading, error } = useConsultationExperts();

  return (
    <section id="experts" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Available Experts
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Choose from our marketplace of verified care placement experts. 
            Each expert sets their own hourly rate and specializes in different areas.
          </p>
        </div>

        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-grandpal-green" />
            <span className="ml-2 text-gray-600">Loading experts...</span>
          </div>
        )}

        {error && (
          <div className="text-center py-12">
            <p className="text-red-600">Error loading experts. Please try again later.</p>
          </div>
        )}

        {!isLoading && !error && (!experts || experts.length === 0) && (
          <ExpertsEmptyState />
        )}

        {!isLoading && !error && experts && experts.length > 0 && (
          <div className="grid md:grid-cols-2 gap-6 max-w-6xl mx-auto">
            {experts.map((expert) => (
              <Card key={expert.id} className="h-full">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <a href={`/consultations/expert/${expert.id}`} tabIndex={0} aria-label={`View profile of ${expert.full_name}`}>
                      <img 
                        src={expert.profile_image_url || "/lovable-uploads/e7b5d364-ad74-4341-aa95-ba4e6d7d1836.png"} 
                        alt={expert.full_name}
                        className="w-16 h-16 rounded-full object-cover flex-shrink-0 hover:shadow-lg transition-shadow"
                      />
                    </a>
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <a href={`/consultations/expert/${expert.id}`} className="hover:underline" tabIndex={0} aria-label={`View profile of ${expert.full_name}`}>
                            <h3 className="text-xl font-bold text-gray-900">{expert.full_name}</h3>
                          </a>
                          <p className="text-grandpal-green-dark font-medium">{expert.professional_title}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-grandpal-green">€{expert.hourly_rate}</p>
                          <p className="text-sm text-gray-600">per hour</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 mb-3">
                        {expert.rating && expert.review_count && (
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                            <span className="text-sm font-medium">{expert.rating}</span>
                            <span className="text-sm text-gray-600">({expert.review_count} reviews)</span>
                          </div>
                        )}
                        <span className="text-sm text-gray-600">{expert.years_experience}+ years experience</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-1 mb-4">
                    {expert.specializations.map((specialty, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {specialty}
                      </Badge>
                    ))}
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-6">
                    {expert.short_description || expert.bio}
                  </p>
                  
                  <div className="flex flex-col gap-2">
                    <Button 
                      className="w-full bg-grandpal-green hover:bg-grandpal-green/90 text-white"
                      onClick={() => {
                        if (expert.google_calendar_link) {
                          window.open(expert.google_calendar_link, '_blank');
                        } else {
                          // Fallback to contact page if no booking link
                          window.location.href = "/contact";
                        }
                      }}
                    >
                      Book Consultation
                      <ExternalLink className="h-4 w-4 ml-2" />
                    </Button>
                    <Button 
                      variant="outline"
                      className="w-full mt-2 border-grandpal-green text-grandpal-green hover:bg-grandpal-green/10 font-semibold"
                      asChild
                    >
                      <a href={`/consultations/expert/${expert.id}`} tabIndex={0} aria-label={`View full profile of ${expert.full_name}`}>
                        View Profile
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
